import React from 'react';

/**
 * Reusable page header component with consistent mobile-responsive styling
 *
 * @param {Object} props
 * @param {React.ElementType} props.icon - Icon component to display
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Optional subtitle/description (hidden on mobile)
 * @param {string} props.count - Optional count to show next to title
 * @param {React.ReactNode} props.stats - Optional stats component to display below header
 * @param {React.ReactNode} props.children - Action buttons or other content
 */
const PageHeader = ({
  icon: Icon,
  title,
  subtitle,
  count,
  stats,
  children
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 shadow">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between py-4 sm:py-6">
          {/* Left side: Icon + Title */}
          <div className="flex items-center min-w-0 flex-1">
            {Icon && (
              <Icon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-400 dark:text-gray-500 mr-2 sm:mr-3 flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {title}
                {count !== undefined && (
                  <span className="ml-2 text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({count})
                  </span>
                )}
              </h1>
              {subtitle && (
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:block mt-0.5">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Right side: Action buttons */}
          {children && (
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2 sm:ml-4">
              {children}
            </div>
          )}
        </div>

        {/* Stats section (optional, displays below header) */}
        {stats && (
          <div className="pb-4 sm:pb-6">
            {stats}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;